import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useHasRole } from '@/hooks/useHasRole';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { RefreshCw, CheckCircle2, XCircle, Clock, AlertTriangle, History, KeyRound } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';

/** Metadata about the last cron secret rotation. */
type RotationInfo = {
  rotated_at?: string;
  previous_expires_at?: string;
  jobs_rewritten?: number;
  jobs_total?: number;
};

type JobStatus = {
  jobid: number;
  jobname: string | null;
  schedule: string;
  active: boolean;
  last_start: string | null;
  last_end: string | null;
  last_status: string | null;
  last_duration_ms: number | null;
  last_return_message: string | null;
  avg_duration_ms_window: number | null;
  max_duration_ms_window: number | null;
  runs_window: number;
  failures_window: number;
};

type WindowKey = '24h' | '7d' | '30d';
const WINDOW_HOURS: Record<WindowKey, number> = { '24h': 24, '7d': 168, '30d': 720 };
const WINDOW_LABEL: Record<WindowKey, string> = { '24h': 'Últimas 24h', '7d': 'Últimos 7 dias', '30d': 'Últimos 30 dias' };

type JobRun = {
  runid: number;
  start_time: string;
  end_time: string | null;
  status: string;
  duration_ms: number | null;
  return_message: string | null;
};

/** Row of public.cron_alerts — rotation failures and rejected (401) cron calls. */
type CronAlertRow = {
  id: string;
  alert_type: string;
  source: string;
  severity: string;
  message: string;
  occurrences: number;
  last_seen_at: string;
  notified_at: string | null;
  created_at: string;
};

const ALERT_TYPE_LABEL: Record<string, string> = {
  rotation_failed: 'Falha na rotação do segredo',
  unauthorized: 'Chamada rejeitada (401)',
};

/** Row of public.cron_run_log — written by the edge functions themselves. */

type CronRunLogRow = {
  id: string;
  job_name: string;
  started_at: string;
  finished_at: string | null;
  duration_ms: number | null;
  status: string;
  items_processed: number | null;
  error: string | null;
};

function formatMs(ms: number | null | undefined) {
  if (ms == null) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)} s`;
  const m = Math.floor(s / 60);
  return `${m}m ${Math.round(s - m * 60)}s`;
}

function formatDateTime(iso: string | null | undefined) {
  if (!iso) return '—';
  try {
    return format(new Date(iso), "dd/MM/yyyy HH:mm:ss", { locale: ptBR });
  } catch {
    return iso;
  }
}

function StatusBadge({ status }: { status: string | null }) {
  if (!status) return <Badge variant="outline">—</Badge>;
  if (status === 'succeeded' || status === 'ok') {
    return <Badge className="bg-success/10 text-success border-success/20"><CheckCircle2 className="h-3 w-3 mr-1" />sucesso</Badge>;
  }
  if (status === 'running' || status === 'starting') {
    return <Badge className="bg-info/10 text-info border-info/20"><Clock className="h-3 w-3 mr-1" />{status}</Badge>;
  }
  if (status === 'skipped') {
    return <Badge variant="outline" className="text-muted-foreground"><Clock className="h-3 w-3 mr-1" />sem trabalho</Badge>;
  }
  return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />{status}</Badge>;
}

export default function AdminCronJobs() {
  const isAdmin = useHasRole('careos_admin');
  const [selected, setSelected] = useState<JobStatus | null>(null);

  const [windowKey, setWindowKey] = useState<WindowKey>('24h');

  const { data, isLoading, isError, error, refetch, isRefetching } = useQuery({
    queryKey: ['admin', 'cron-jobs', windowKey],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_cron_jobs_status', {
        p_hours: WINDOW_HOURS[windowKey],
      } as never);
      if (error) throw error;
      return ((data ?? []) as unknown) as JobStatus[];
    },
    enabled: isAdmin === true,
    refetchInterval: 30_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const runs = useQuery({
    queryKey: ['admin', 'cron-job-runs', selected?.jobid],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_cron_job_runs', {
        p_jobid: selected!.jobid,
        p_limit: 50,
      });
      if (error) throw error;
      return (data ?? []) as JobRun[];
    },
    enabled: !!selected,
    retry: false,
  });

  // Execution log written by the functions themselves — survives the short
  // retention of the platform's edge logs.
  const runLog = useQuery({
    queryKey: ['admin', 'cron-run-log', windowKey],
    queryFn: async () => {
      const since = new Date(Date.now() - WINDOW_HOURS[windowKey] * 3600_000).toISOString();
      const { data, error } = await supabase
        .from('cron_run_log' as never)
        .select('id, job_name, started_at, finished_at, duration_ms, status, items_processed, error')
        .gte('started_at', since)
        .order('started_at', { ascending: false })
        .limit(100);
      if (error) throw error;
      return ((data ?? []) as unknown) as CronRunLogRow[];
    },
    enabled: isAdmin === true,
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  // Cron secret rotation state (public.system_settings key = cron_secret_rotation).
  const rotation = useQuery({
    queryKey: ['admin', 'cron-secret-rotation'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'cron_secret_rotation')
        .maybeSingle();
      if (error) throw error;
      return (data?.value ?? null) as RotationInfo | null;
    },
    enabled: isAdmin === true,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const [rotating, setRotating] = useState(false);
  const handleRotate = async () => {
    setRotating(true);
    try {
      const { data, error } = await supabase.rpc('admin_rotate_cron_secret' as never);
      if (error) throw error;
      toast.success(`Segredo trocado. Jobs atualizados: ${Number(data ?? 0)}. O segredo anterior segue válido por 48h.`);
      await rotation.refetch();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Não foi possível trocar o segredo.');
    } finally {
      setRotating(false);
    }
  };

  // Operational alerts: failed secret rotations and 401s from the edge functions.
  const alerts = useQuery({
    queryKey: ['admin', 'cron-alerts', windowKey],
    queryFn: async () => {
      const since = new Date(Date.now() - WINDOW_HOURS[windowKey] * 3600_000).toISOString();
      const { data, error } = await supabase
        .from('cron_alerts' as never)
        .select('id, alert_type, source, severity, message, occurrences, last_seen_at, notified_at, created_at')
        .gte('created_at', since)
        .order('created_at', { ascending: false })
        .limit(50);
      if (error) throw error;
      return ((data ?? []) as unknown) as CronAlertRow[];
    },
    enabled: isAdmin === true,
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });


  if (isAdmin === null) {
    return (
      <AppLayout title="Jobs agendados">
        <div className="p-4"><Skeleton className="h-10 w-48" /></div>
      </AppLayout>
    );
  }
  if (isAdmin === false) return <Navigate to="/app" replace />;

  const jobs = data ?? [];
  const totalFailures = jobs.reduce((acc, j) => acc + (j.failures_window ?? 0), 0);
  const totalRuns = jobs.reduce((acc, j) => acc + (j.runs_window ?? 0), 0);
  const inactive = jobs.filter((j) => !j.active).length;
  const windowLabel = WINDOW_LABEL[windowKey];

  return (
    <AppLayout title="Jobs agendados">
      <div className="container mx-auto p-4 space-y-4 max-w-7xl">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold">Jobs agendados (pg_cron)</h1>
            <p className="text-sm text-muted-foreground">
              Status, última execução e tempo médio das tarefas recorrentes no banco.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex rounded-md border bg-background p-0.5">
              {(['24h', '7d', '30d'] as WindowKey[]).map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setWindowKey(k)}
                  className={`px-3 py-1 text-xs rounded-sm transition-colors ${
                    windowKey === k
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {WINDOW_LABEL[k]}
                </button>
              ))}
            </div>
            <Button size="sm" variant="outline" onClick={() => refetch()} disabled={isRefetching}>
              <RefreshCw className={`h-4 w-4 mr-1 ${isRefetching ? 'animate-spin' : ''}`} />
              Atualizar
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card><CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Total de jobs</p>
            <p className="text-2xl font-bold">{jobs.length}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Inativos</p>
            <p className={`text-2xl font-bold ${inactive > 0 ? 'text-warning' : ''}`}>{inactive}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Execuções ({windowLabel.toLowerCase()})</p>
            <p className="text-2xl font-bold">{totalRuns.toLocaleString('pt-BR')}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Falhas ({windowLabel.toLowerCase()})</p>
            <p className={`text-2xl font-bold ${totalFailures > 0 ? 'text-destructive' : 'text-success'}`}>
              {totalFailures.toLocaleString('pt-BR')}
            </p>
          </CardContent></Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <KeyRound className="h-4 w-4" />
              Segredo dos jobs (rotação)
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 flex flex-wrap items-end justify-between gap-4">
            <div className="text-sm space-y-1">
              <p className="text-muted-foreground text-xs">
                Troca automática todo dia 1 às 04:00 UTC. O segredo anterior continua aceito por 48h,
                então nenhum job falha durante a troca.
              </p>
              <p>
                Última troca:{' '}
                <span className="font-medium">{formatDateTime(rotation.data?.rotated_at)}</span>
              </p>
              <p>
                Segredo anterior válido até:{' '}
                <span className="font-medium">{formatDateTime(rotation.data?.previous_expires_at)}</span>
              </p>
            </div>
            <Button size="sm" variant="outline" onClick={handleRotate} disabled={rotating}>
              <RefreshCw className={`h-4 w-4 mr-1 ${rotating ? 'animate-spin' : ''}`} />
              Trocar agora
            </Button>
          </CardContent>
        </Card>

        <Card className={(alerts.data?.length ?? 0) > 0 ? 'border-destructive/40' : undefined}>
          <CardHeader className="flex flex-row items-center justify-between gap-2">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className={`h-4 w-4 ${(alerts.data?.length ?? 0) > 0 ? 'text-destructive' : 'text-muted-foreground'}`} />
              Alertas ({windowLabel.toLowerCase()})
            </CardTitle>
            <Badge variant={(alerts.data?.length ?? 0) > 0 ? 'destructive' : 'outline'}>
              {alerts.data?.length ?? 0}
            </Badge>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <p className="text-xs text-muted-foreground mb-3">
              Falhas na rotação do segredo e chamadas rejeitadas com 401. Um resumo é enviado
              por e-mail aos administradores a cada hora enquanto houver alertas novos.
            </p>
            {alerts.isLoading && <Skeleton className="h-16" />}
            {!alerts.isLoading && (alerts.data?.length ?? 0) === 0 && (
              <p className="text-sm text-success flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" /> Nenhum alerta no período.
              </p>
            )}
            {(alerts.data?.length ?? 0) > 0 && (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Origem</TableHead>
                      <TableHead>Detalhe</TableHead>
                      <TableHead className="text-right">Ocorrências</TableHead>
                      <TableHead>Última</TableHead>
                      <TableHead>E-mail</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(alerts.data ?? []).map((a) => (
                      <TableRow key={a.id}>
                        <TableCell className={a.severity === 'critical' ? 'text-destructive font-medium' : 'font-medium'}>
                          {ALERT_TYPE_LABEL[a.alert_type] ?? a.alert_type}
                        </TableCell>
                        <TableCell className="font-mono text-xs">{a.source}</TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-[320px]">{a.message}</TableCell>
                        <TableCell className="text-right">{a.occurrences}</TableCell>
                        <TableCell className="text-xs">{formatDateTime(a.last_seen_at)}</TableCell>
                        <TableCell className="text-xs">
                          {a.notified_at ? (
                            <span className="text-muted-foreground">enviado</span>
                          ) : (
                            <Badge variant="outline">pendente</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>


        {isLoading && <Skeleton className="h-64" />}
        {isError && (
          <Card><CardContent className="p-4 text-sm text-destructive">
            <p>Erro ao carregar jobs. Verifique se você tem o papel <code>careos_admin</code>.</p>
            {error instanceof Error && <p className="mt-2 text-xs opacity-80">{error.message}</p>}
          </CardContent></Card>
        )}

        {!isLoading && !isError && (
          <Card>
            <CardHeader><CardTitle className="text-base">Todos os jobs</CardTitle></CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome</TableHead>
                      <TableHead>Agendamento</TableHead>
                      <TableHead>Ativo</TableHead>
                      <TableHead>Último status</TableHead>
                      <TableHead>Última execução</TableHead>
                      <TableHead className="text-right">Última duração</TableHead>
                      <TableHead className="text-right">Média ({windowLabel.toLowerCase()})</TableHead>
                      <TableHead className="text-right">Máx ({windowLabel.toLowerCase()})</TableHead>
                      <TableHead className="text-right">Falhas / Total</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {jobs.map((j) => {
                      const failureRate = j.runs_window > 0 ? j.failures_window / j.runs_window : 0;
                      const rowWarn = !j.active || j.last_status === 'failed' || failureRate >= 0.1;
                      return (
                        <TableRow key={j.jobid} className={rowWarn ? 'bg-destructive/5' : undefined}>
                          <TableCell className="font-medium">{j.jobname ?? `#${j.jobid}`}</TableCell>
                          <TableCell><code className="text-xs">{j.schedule}</code></TableCell>
                          <TableCell>
                            {j.active
                              ? <Badge variant="outline" className="text-success border-success/30">sim</Badge>
                              : <Badge variant="outline" className="text-warning border-warning/30"><AlertTriangle className="h-3 w-3 mr-1" />não</Badge>}
                          </TableCell>
                          <TableCell><StatusBadge status={j.last_status} /></TableCell>
                          <TableCell className="text-xs">{formatDateTime(j.last_start)}</TableCell>
                          <TableCell className="text-right tabular-nums">{formatMs(j.last_duration_ms)}</TableCell>
                          <TableCell className="text-right tabular-nums">{formatMs(j.avg_duration_ms_window)}</TableCell>
                          <TableCell className="text-right tabular-nums">{formatMs(j.max_duration_ms_window)}</TableCell>
                          <TableCell className="text-right tabular-nums">
                            <span className={j.failures_window > 0 ? 'text-destructive font-semibold' : ''}>
                              {j.failures_window}
                            </span>
                            <span className="text-muted-foreground"> / {j.runs_window}</span>
                          </TableCell>
                          <TableCell>
                            <Button size="sm" variant="ghost" onClick={() => setSelected(j)}>
                              <History className="h-4 w-4 mr-1" />Histórico
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {jobs.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={10} className="text-center text-muted-foreground py-6">
                          Nenhum job agendado encontrado.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Execuções das funções ({windowLabel.toLowerCase()})</CardTitle>
            <p className="text-xs text-muted-foreground">
              Registro gravado pelas próprias funções (duração real, itens processados e erro),
              independente do log temporário da plataforma.
            </p>
          </CardHeader>
          <CardContent className="p-0">
            {runLog.isLoading && <div className="p-4"><Skeleton className="h-32" /></div>}
            {runLog.isError && (
              <p className="p-4 text-sm text-destructive">Erro ao carregar o registro de execuções.</p>
            )}
            {runLog.data && (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Função</TableHead>
                      <TableHead>Início</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Duração</TableHead>
                      <TableHead className="text-right">Itens</TableHead>
                      <TableHead>Erro</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {runLog.data.map((r) => (
                      <TableRow key={r.id} className={r.status === 'failed' ? 'bg-destructive/5' : undefined}>
                        <TableCell className="font-medium">{r.job_name}</TableCell>
                        <TableCell className="text-xs">{formatDateTime(r.started_at)}</TableCell>
                        <TableCell><StatusBadge status={r.status} /></TableCell>
                        <TableCell className="text-right tabular-nums">{formatMs(r.duration_ms)}</TableCell>
                        <TableCell className="text-right tabular-nums">{r.items_processed ?? '—'}</TableCell>
                        <TableCell className="text-xs max-w-[320px] truncate" title={r.error ?? ''}>
                          {r.error ?? '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                    {runLog.data.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-muted-foreground py-6">
                          Nenhuma execução registrada nesse período.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>



        <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>
                Histórico — {selected?.jobname ?? `#${selected?.jobid}`}
              </DialogTitle>
            </DialogHeader>
            {runs.isLoading && <Skeleton className="h-40" />}
            {runs.data && (
              <div className="max-h-[60vh] overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Início</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Duração</TableHead>
                      <TableHead>Mensagem</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {runs.data.map((r) => (
                      <TableRow key={r.runid}>
                        <TableCell className="text-xs">{formatDateTime(r.start_time)}</TableCell>
                        <TableCell><StatusBadge status={r.status} /></TableCell>
                        <TableCell className="text-right tabular-nums">{formatMs(r.duration_ms)}</TableCell>
                        <TableCell className="text-xs max-w-[400px] truncate" title={r.return_message ?? ''}>
                          {r.return_message ?? '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                    {runs.data.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-muted-foreground py-4">
                          Sem execuções registradas.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
